import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  Card, 
  Alert, 
  Spinner, 
  Badge, 
  Modal, 
  ListGroup 
} from 'react-bootstrap';
import Select from 'react-select';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getFieldMappings, 
  saveFieldMappings, 
  testFieldMappings,
  getSalesforceObjectFields,
  updateMappingMode,
  bulkUpdatePerContactMappings,
  getAutoStageChangeSettings,
  updateAutoStageChangeSettings,
  getOpportunityStageValues
} from '../api/fieldMappings';
import FieldMappingsSideNav from '../components/FieldMappingsSideNav';
import MappingModeToggle from '../components/MappingModeToggle';
import '../styles/FieldMappings.css';
import { FaCheck, FaSync, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

/**
 * Field Mappings page
 * Allows admin users to map application fields to Salesforce fields
 */
const FieldMappings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Hardcoded opportunity fields (form fields from the application)
  const hardcodedOpportunityFields = [
    // Business
    { name: 'BusinessLegalName', label: 'Business Legal Name', type: 'string', category: 'Business' },
    { name: 'DBA', label: 'DBA', type: 'string', category: 'Business' },
    { name: 'Industry', label: 'Industry', type: 'string', category: 'Business' },
    { name: 'EntityType', label: 'Entity Type', type: 'string', category: 'Business' },
    { name: 'FederalTaxID', label: 'Federal Tax ID', type: 'string', category: 'Business' },
    { name: 'AnnualRevenue', label: 'Annual Revenue', type: 'string', category: 'Business' },
    { name: 'AverageMonthlySales', label: 'Average Monthly Sales', type: 'string', category: 'Business' },
    { name: 'AverageNSFs', label: 'Average NSFs', type: 'number', category: 'Business' },
    { name: 'AverageNumberOfDeposits', label: 'Average Number of Deposits', type: 'number', category: 'Business' },
    { name: 'AverageNegativeDaysPerMonth', label: 'Average Negative Days Per Month', type: 'number', category: 'Business' },
    { name: 'RequestedAmount', label: 'Requested Amount', type: 'string', category: 'Business' },
    { name: 'AverageBankBalance', label: 'Average Bank Balance', type: 'string', category: 'Business' },
    { name: 'BusinessStartDate', label: 'Business Start Date', type: 'date', category: 'Business' },
    { name: 'SICCode', label: 'SIC Code', type: 'string', category: 'Business' },
    { name: 'NumberOfPositions', label: 'Number of Positions', type: 'number', category: 'Business' },
    { name: 'PayoffAmountNeeded', label: 'Payoff Amount Needed', type: 'number', category: 'Business' },
    { name: 'DealNotes', label: 'Deal Notes', type: 'textarea', category: 'Business' },
    
    // Business Address
    { name: 'BusinessStreet', label: 'Street', type: 'string', category: 'Business Address' },
    { name: 'BusinessCity', label: 'City', type: 'string', category: 'Business Address' },
    { name: 'BusinessState', label: 'State', type: 'string', category: 'Business Address' },
    { name: 'BusinessZipCode', label: 'Zip Code', type: 'string', category: 'Business Address' },
    
    // Primary Contact
    { name: 'ContactFirstName', label: 'First Name', type: 'string', category: 'Primary Contact' },
    { name: 'ContactLastName', label: 'Last Name', type: 'string', category: 'Primary Contact' },
    { name: 'ContactTitle', label: 'Title', type: 'string', category: 'Primary Contact' },
    { name: 'ContactSSN', label: 'SSN', type: 'string', category: 'Primary Contact' },
    { name: 'ContactDateOfBirth', label: 'Date of Birth', type: 'date', category: 'Primary Contact' },
    { name: 'ContactEmail', label: 'Email', type: 'string', category: 'Primary Contact' },
    { name: 'ContactCellPhone', label: 'Cell Phone', type: 'string', category: 'Primary Contact' },
    { name: 'ContactOwnershipPercentage', label: 'Ownership Percentage', type: 'string', category: 'Primary Contact' },
    { name: 'ContactCreditScore', label: 'Credit Score', type: 'string', category: 'Primary Contact' },
    { name: 'CumulativeOwnershipPercentage', label: 'Cumulative Ownership Percentage', type: 'number', category: 'Primary Contact' },
    
    // Secondary Contact
    { name: 'Contact2FirstName', label: 'First Name', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2LastName', label: 'Last Name', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2Title', label: 'Title', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2SSN', label: 'SSN', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2DateOfBirth', label: 'Date of Birth', type: 'date', category: 'Secondary Contact' },
    { name: 'Contact2Email', label: 'Email', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2CellPhone', label: 'Cell Phone', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2OwnershipPercentage', label: 'Ownership Percentage', type: 'string', category: 'Secondary Contact' },
    { name: 'Contact2CreditScore', label: 'Credit Score', type: 'string', category: 'Secondary Contact' },
    
    // Contact Address
    { name: 'ContactStreet', label: 'Street', type: 'string', category: 'Contact Address' },
    { name: 'ContactCity', label: 'City', type: 'string', category: 'Contact Address' },
    { name: 'ContactState', label: 'State', type: 'string', category: 'Contact Address' },
    { name: 'ContactZipCode', label: 'Zip Code', type: 'string', category: 'Contact Address' },
    
    // Secondary Contact Address
    { name: 'Contact2Street', label: 'Street', type: 'string', category: 'Secondary Contact Address' },
    { name: 'Contact2City', label: 'City', type: 'string', category: 'Secondary Contact Address' },
    { name: 'Contact2State', label: 'State', type: 'string', category: 'Secondary Contact Address' },
    { name: 'Contact2ZipCode', label: 'Zip Code', type: 'string', category: 'Secondary Contact Address' },
    
    // General
    { name: 'Notes', label: 'Notes', type: 'string', category: 'General' },
    { name: 'ReferralSource', label: 'Referral Source', type: 'string', category: 'General' }
  ];
  
  const [opportunityFields, setOpportunityFields] = useState([]);
  const [salesforceFields, setSalesforceFields] = useState({
    Opportunity: [],
    Account: [],
    Contact: []
  });
  const [mappings, setMappings] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTestResults, setShowTestResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedObject, setSelectedObject] = useState('Opportunity');
  
  // NEW STATE for per-contact mapping functionality
  const [mappingMode, setMappingMode] = useState('shared'); // 'shared' or 'per_contact'
  const [perContactMappings, setPerContactMappings] = useState({}); // Field-specific contact mappings
  const [modeChangePending, setModeChangePending] = useState(false);
  
  // NEW STATE for auto stage change functionality
  const [autoStageChangeSettings, setAutoStageChangeSettings] = useState({
    enabled: false,
    stageName: '',
    stageLabel: ''
  });
  const [stageValues, setStageValues] = useState([]);
  const [stageSaving, setStageSaving] = useState(false);
  
  // Available Salesforce objects
  const salesforceObjects = [
    { value: 'Opportunity', label: 'Opportunity' },
    { value: 'Account', label: 'Account' },
    { value: 'Contact', label: 'Contact' }
  ];
  
  // Check if user is admin or developer
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'developer') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Update categories when mapping mode changes
  useEffect(() => {
    let allCategories = [
      ...new Set(hardcodedOpportunityFields.map(field => field.category || 'General'))
    ];
    
    // Filter out secondary contact categories when in shared mode
    const secondaryContactCategories = ['Secondary Contact', 'Secondary Contact Address'];
    let filteredCategories = allCategories;
    
    if (mappingMode === 'shared') {
      // Hide secondary contact categories in shared mode
      filteredCategories = allCategories.filter(category => 
        !secondaryContactCategories.includes(category)
      );
      console.log(`[DEBUG] Mapping mode changed to shared: Hiding secondary contact categories`);
    } else {
      // Show all categories in per-contact mode
      console.log(`[DEBUG] Mapping mode changed to per-contact: Showing all categories including secondary contacts`);
    }
    
    setCategories(filteredCategories);
    
    // Reset active section to 0 if current section is no longer available
    if (activeSection >= filteredCategories.length) {
      setActiveSection(0);
    }
  }, [mappingMode, activeSection]);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Starting to load field mapping data...');
        setLoading(true);
        setError(null);
        
        // Set opportunity fields directly from hardcoded data
        setOpportunityFields(hardcodedOpportunityFields);
        console.log('Set opportunity fields from hardcoded data:');
        
        // Extract categories from opportunity fields
        let allCategories = [
          ...new Set(hardcodedOpportunityFields.map(field => field.category || 'General'))
        ];
        
        // Filter out secondary contact categories when in shared mode
        const secondaryContactCategories = ['Secondary Contact', 'Secondary Contact Address'];
        let filteredCategories = allCategories;
        
        if (mappingMode === 'shared') {
          // Hide secondary contact categories in shared mode
          filteredCategories = allCategories.filter(category => 
            !secondaryContactCategories.includes(category)
          );
          console.log('Initial load - Shared mode: Hiding secondary contact categories');
        } else {
          // Show all categories in per-contact mode
          console.log('Initial load - Per-contact mode: Showing all categories including secondary contacts');
        }
        
        setCategories(filteredCategories);
        console.log('Set categories:', filteredCategories);
        
        // Fetch existing primary field mappings
        console.log('Fetching existing field mappings...');
        const existingMappings = await getFieldMappings();
        console.log('Received existing mappings:', existingMappings);
        
        // Process mappings while preserving all fields including required
        const updatedMappings = existingMappings.map(mapping => ({
          ...mapping,
          salesforceObject: mapping.salesforceObject || 'Opportunity',
          // Explicitly preserve required field - default to true if not set
          required: mapping.required !== undefined ? mapping.required : true
        }));
        
        setMappings(updatedMappings);
        console.log('Updated primary mappings with required field info:', 
          updatedMappings.map(m => ({ field: m.opportunityField, required: m.required })));
        
        // Preload fields for ALL Salesforce objects at once
        console.log('Preloading fields for all Salesforce objects...');
        
        const fieldPromises = salesforceObjects.map(async (obj) => {
          console.log(`Loading fields for ${obj.value}...`);
          try {
            const fields = await getSalesforceObjectFields(obj.value);
            console.log(`Successfully loaded ${fields.length} fields for ${obj.value}.`);
            return { objectName: obj.value, fields };
          } catch (err) {
            console.error(`Error loading fields for ${obj.value}:`, err);
            return { objectName: obj.value, fields: [] }; // Return empty on error
          }
        });
        
        // Wait for all field loading to complete
        const results = await Promise.all(fieldPromises);
        
        // Update state with all fields at once
        const allFields = results.reduce((acc, result) => {
          acc[result.objectName] = result.fields;
          return acc;
        }, {});
        
        console.log('All Salesforce fields summary: Checking loaded fields...');
        Object.keys(allFields).forEach(objectName => {
          if (!allFields[objectName] || allFields[objectName].length === 0) {
            console.warn(`- WARNING: ${objectName}: No fields loaded or empty array received.`);
          } else {
            console.log(`- ${objectName}: ${allFields[objectName].length} fields loaded successfully.`);
          }
        });
        
        setSalesforceFields(allFields);
        console.log('All Salesforce fields preloaded successfully.');
        
        // Load auto stage change settings and stage values
        console.log('Loading auto stage change settings and stage values...');
        try {
          const [stageSettingsResponse, stageValuesResponse] = await Promise.all([
            getAutoStageChangeSettings(),
            getOpportunityStageValues()
          ]);
          
          if (stageSettingsResponse.success && stageSettingsResponse.setting) {
            setAutoStageChangeSettings(stageSettingsResponse.setting);
            console.log('Loaded auto stage change settings:', stageSettingsResponse.setting);
          }
          
          if (stageValuesResponse.success && stageValuesResponse.stageValues) {
            setStageValues(stageValuesResponse.stageValues);
            console.log(`Loaded ${stageValuesResponse.stageValues.length} stage values`);
          }
        } catch (stageError) {
          console.error('Error loading stage settings:', stageError);
          // Don't fail the whole page load if stage settings fail
        }
        
        // Log mapping analysis
        logMappingAnalysis(hardcodedOpportunityFields, updatedMappings, allFields);
        
        console.log('Field mapping data loading complete.');
        setLoading(false);
      } catch (err) {
        console.error('Critical error during data loading:', err);
        setError(`Error loading data: ${err.message || 'Please try again.'}`);
        setLoading(false);
      }
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Keep dependency array empty for initial load
  
  // Function to log detailed mapping analysis
  const logMappingAnalysis = (opportunityFields, mappings, salesforceFields) => {
    console.group('DETAILED FIELD MAPPING ANALYSIS');
    console.log('----------');
    
    // Group opportunity fields by category for better readability
    const fieldsByCategory = {};
    opportunityFields.forEach(field => {
      if (!fieldsByCategory[field.category]) {
        fieldsByCategory[field.category] = [];
      }
      fieldsByCategory[field.category].push(field);
    });
    
    // Analyze each category
    Object.entries(fieldsByCategory).forEach(([category, fields]) => {
      console.group(`Category: ${category}`);
      
      fields.forEach(field => {
        const mapping = mappings.find(m => m.opportunityField === field.name);
        
        if (!mapping || !mapping.salesforceField) {
          console.log(`❌ ${field.name} (${field.label}) - NOT MAPPED`);
        } else {
          const sfObject = mapping.salesforceObject;
          const sfField = mapping.salesforceField;
          const sfFieldExists = salesforceFields[sfObject]?.some(f => f.name === sfField);
          
          if (sfFieldExists) {
            const sfFieldDetails = salesforceFields[sfObject].find(f => f.name === sfField);
            console.log(`✅ ${field.name} (${field.label}) → ${sfObject}.${sfField} (${sfFieldDetails.label || 'No Label'})`);
          } else {
            console.log(`⚠️ ${field.name} (${field.label}) → ${sfObject}.${sfField} - WARNING: Target field might not exist!`);
          }
        }
      });
      
      console.groupEnd();
    });
    
    // Count mapping statistics
    const totalFields = opportunityFields.length;
    const mappedFields = mappings.filter(m => m.salesforceField).length;
    const mappedPercentage = Math.round((mappedFields / totalFields) * 100);
    
    console.log('----------');
    console.log(`SUMMARY: ${mappedFields} of ${totalFields} fields mapped (${mappedPercentage}%)`);
    console.groupEnd();
  };
  
  // Calculate mapping stats for sidebar
  const calculateMappingStats = () => {
    const stats = {};
    
    categories.forEach(category => {
      const categoryFields = opportunityFields.filter(field => field.category === category);
      const mappedFieldsCount = categoryFields.filter(field => {
        const mapping = mappings.find(m => m.opportunityField === field.name);
        return mapping && mapping.salesforceField;
      }).length;
      
      stats[category] = {
        mapped: mappedFieldsCount,
        total: categoryFields.length
      };
    });
    
    console.log('Mapping stats:', stats);
    return stats;
  };
  
  // Handle object selection change for a specific mapping
  const handleMappingObjectChange = async (opportunityField, selectedOption) => {
    if (!selectedOption) return;
    
    const objectName = selectedOption.value;
    console.log('handleMappingObjectChange called with:', opportunityField, objectName);
    
    // Update the specific mapping without making API calls
    const newMappings = [...mappings];
    const mappingIndex = newMappings.findIndex(m => m.opportunityField === opportunityField);
    
    if (mappingIndex >= 0) {
      // Update existing mapping
      newMappings[mappingIndex] = {
        ...newMappings[mappingIndex],
        salesforceObject: objectName,
        salesforceField: '' // Reset the field when object changes
      };
    } else {
      // Add new mapping
      newMappings.push({
        opportunityField,
        salesforceObject: objectName,
        salesforceField: ''
      });
    }
    
    console.log('Setting mappings to:', newMappings);
    setMappings(newMappings);
  };
  
  // Handle default object selection change
  const handleObjectChange = (value) => {
    console.log('Changing default object to:', value);
    setSelectedObject(value);
  };
  
  // Handle field selection change
  const handleFieldChange = (opportunityField, salesforceField) => {
    console.log('handleFieldChange called with:', opportunityField, salesforceField);
    
    // Create a new array of mappings
    const newMappings = [...mappings];
    const mappingIndex = newMappings.findIndex(m => m.opportunityField === opportunityField);
    
    if (mappingIndex >= 0) {
      // Update existing mapping
      newMappings[mappingIndex] = {
        ...newMappings[mappingIndex],
        salesforceField
      };
    } else {
      // Add new mapping
      newMappings.push({
        opportunityField,
        salesforceObject: selectedObject,
        salesforceField
      });
    }
    
    console.log('Setting mappings to:', newMappings);
    setMappings(newMappings);
  };
  
  // Handle required toggle change
  const handleRequiredToggle = (opportunityField, isRequired) => {
    console.log('handleRequiredToggle called with:', opportunityField, isRequired);
    
    // Create a new array of mappings
    const newMappings = [...mappings];
    const mappingIndex = newMappings.findIndex(m => m.opportunityField === opportunityField);
    
    if (mappingIndex >= 0) {
      // Update existing mapping
      newMappings[mappingIndex] = {
        ...newMappings[mappingIndex],
        required: isRequired
      };
      console.log('Updated existing mapping:', newMappings[mappingIndex]);
    } else {
      // Add new mapping with required field
      const newMapping = {
        opportunityField,
        salesforceObject: selectedObject,
        salesforceField: '',
        required: isRequired
      };
      newMappings.push(newMapping);
      console.log('Added new mapping:', newMapping);
    }
    
    console.log('New mappings state:', newMappings.filter(m => m.opportunityField === opportunityField));
    console.log('Full mappings array length:', newMappings.length);
    setMappings(newMappings);
  };
  
  // Custom styles for Select component
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
      boxShadow: 'none',
      borderColor: '#ced4da',
      fontSize: '0.85rem',
      '&:hover': {
        borderColor: '#80bdff'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '0.85rem'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    container: (provided) => ({
      ...provided,
      width: '100%'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
      height: '30px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      margin: '3px 0'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '3px 8px'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '3px 8px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2e3e9f' : state.isFocused ? '#e0e4ff' : null,
      color: state.isSelected ? 'white' : 'inherit',
      cursor: 'pointer',
      fontSize: '0.85rem',
      padding: '5px 12px',
      ':active': {
        backgroundColor: state.isSelected ? '#2e3e9f' : '#c5cdff'
      },
      ':hover': {
        backgroundColor: state.isSelected ? '#2e3e9f' : '#e0e4ff'
      },
      fontWeight: state.isSelected ? '500' : 'normal',
      transition: 'background-color 0.1s ease',
      borderLeft: state.isSelected ? '3px solid #2e3e9f' : state.isFocused ? '3px solid #9aa6ff' : '3px solid transparent',
      paddingLeft: state.isSelected || state.isFocused ? '9px' : '12px' // Adjust padding to compensate for border
    })
  };
  
  // Save mappings
  const handleSave = async () => {
    console.log('🚀 handleSave called!');
    try {
      if (saving) {
        console.log('❌ Already saving, returning early');
        return;
      }
      console.log('✅ Starting save process...');
      setSaving(true);
      setError(null);
      setSuccess(null);
  
      // Create mappings for all opportunity fields, including their required status
      const allFieldMappings = [];
      
      // Get all opportunity fields from the hardcoded list
      hardcodedOpportunityFields.forEach(field => {
        const existingMapping = mappings.find(m => m.opportunityField === field.name);
        
        if (existingMapping) {
          // Always include the mapping, whether complete or not
          allFieldMappings.push({
            ...existingMapping,
            // Ensure required field is preserved - default to true if not set
            required: existingMapping.required !== undefined ? existingMapping.required : true
          });
        } else {
          // Create a new mapping with just the required field set
          allFieldMappings.push({
            opportunityField: field.name,
            required: true // Default to required for new fields
          });
        }
      });
      
      console.log('Saving All Field Mappings with Required Status:', allFieldMappings);
      console.log('Required field summary:', allFieldMappings.map(m => ({ 
        field: m.opportunityField, 
        required: m.required,
        hasRequiredField: m.required !== undefined
      })));
  
      // Save field mappings
      await saveFieldMappings(allFieldMappings);
  
      setSuccess('Field mappings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setSaving(false);
    } catch (err) {
      console.error('Error saving data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Error saving data. Please try again.';
      setError(errorMessage);
      setSaving(false);
    }
  };
  
  // Test mappings
  const handleTest = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      
      // Filter out mappings that don't have both required fields
      const validMappings = mappings.filter(mapping => 
        mapping.opportunityField && mapping.salesforceField
      );
      
      if (validMappings.length === 0) {
        setError('No valid mappings to test. Please map at least one field to Salesforce.');
        setTesting(false);
        return;
      }
      
      const result = await testFieldMappings(validMappings);
      setTestResults(result);
      setShowTestResults(true);
      
      setTesting(false);
    } catch (err) {
      console.error('Error testing field mappings:', err);
      setError('Error testing field mappings. Please try again.');
      setTesting(false);
    }
  };
  
  // Filter fields by search term and active category
  const getFilteredFields = () => {
    const activeCategory = categories[activeSection];
    
    const filteredFields = opportunityFields.filter(field => {
      const matchesCategory = field.category === activeCategory;
      const matchesSearch = !searchTerm || 
        field.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
        field.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
    
    console.log(`Filtered fields for category "${activeCategory}":`, filteredFields.map(f => f.name));
    return filteredFields;
  };
  
  // Helper function to get mapping for a specific field
  const getMappingForField = (opportunityField) => {
    // Find the mapping for this opportunity field
    const mapping = mappings.find(m => m.opportunityField === opportunityField);
    
    // Return the mapping or a default empty mapping
    const result = mapping || { 
      opportunityField,
      salesforceObject: selectedObject,
      salesforceField: '',
      required: true // Default to required for new mappings
    };
    
    // Log when a field has no mapping
    if (!mapping || !mapping.salesforceField) {
      console.log(`No mapping found for field: ${opportunityField}`);
    }
    
    return result;
  };
  
  // Close test results modal
  const handleCloseTestResults = () => {
    setShowTestResults(false);
  };
  
  // Handle mapping mode toggle
  const handleMappingModeToggle = async (newMode) => {
    try {
      setModeChangePending(true);
      setError(null);
      
      console.log(`[DEBUG] Switching mapping mode from ${mappingMode} to ${newMode}`);
      console.log(`[DEBUG] Current mappings:`, mappings);
      setMappingMode(newMode);
      
      // If switching to per-contact mode, initialize contact mappings for existing fields
      if (newMode === 'per_contact') {
        const initialPerContactMappings = {};
        
        // Initialize for all form fields, not just those with existing mappings
        hardcodedOpportunityFields.forEach(field => {
          const existingMapping = mappings.find(m => m.opportunityField === field.name);
          console.log(`[DEBUG] Processing field ${field.name}:`, { field, existingMapping });
          
          // Always initialize the field structure, even if no existing mapping
          initialPerContactMappings[field.name] = {
            opportunityField: field.name,
            mappingMode: 'per_contact',
            salesforceField: existingMapping?.salesforceField || '',
            salesforceObject: existingMapping?.salesforceObject || 'Contact',
            contactMappings: [
              {
                contactNumber: 1,
                salesforceField: existingMapping?.salesforceField || '',
                salesforceObject: existingMapping?.salesforceObject || 'Contact',
                enabled: !!existingMapping?.salesforceField
              },
              {
                contactNumber: 2,
                salesforceField: '',
                salesforceObject: 'Contact',
                enabled: false
              },
              {
                contactNumber: 3,
                salesforceField: '',
                salesforceObject: 'Contact',
                enabled: false
              }
            ]
          };
        });
        
        console.log(`[DEBUG] Initialized per-contact mappings:`, initialPerContactMappings);
        setPerContactMappings(initialPerContactMappings);
      } else {
        // Switching back to shared mode
        setPerContactMappings({});
      }
      
      setSuccess(`Switched to ${newMode === 'shared' ? 'shared' : 'per-contact'} mapping mode`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error changing mapping mode:', error);
      setError('Failed to change mapping mode');
    } finally {
      setModeChangePending(false);
    }
  };
  
  // Handle contact mapping changes for individual fields
  const handleContactMappingChange = (opportunityField, contactNumber, updatedMapping) => {
    console.log(`[DEBUG] handleContactMappingChange called:`, {
      opportunityField,
      contactNumber,
      updatedMapping
    });
    
    setPerContactMappings(prev => {
      const fieldMappings = prev[opportunityField] || { contactMappings: [] };
      const updatedContactMappings = [...(fieldMappings.contactMappings || [])];
      
      // Find existing contact mapping or create new one
      const existingIndex = updatedContactMappings.findIndex(cm => cm.contactNumber === contactNumber);
      
      if (existingIndex >= 0) {
        updatedContactMappings[existingIndex] = updatedMapping;
      } else {
        updatedContactMappings.push(updatedMapping);
      }
      
      const newState = {
        ...prev,
        [opportunityField]: {
          ...fieldMappings,
          contactMappings: updatedContactMappings
        }
      };
      
      console.log(`[DEBUG] Updated perContactMappings for ${opportunityField}:`, newState[opportunityField]);
      return newState;
    });
  };
  
  // Handle auto stage change setting changes
  const handleAutoStageChangeToggle = async (enabled) => {
    try {
      setStageSaving(true);
      setError(null);
      
      const updatedSettings = {
        ...autoStageChangeSettings,
        enabled
      };
      
      const response = await updateAutoStageChangeSettings(updatedSettings);
      
      if (response.success) {
        setAutoStageChangeSettings(response.setting);
        setSuccess(`Auto stage change ${enabled ? 'enabled' : 'disabled'} successfully`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error updating auto stage change setting:', error);
      setError('Failed to update auto stage change setting');
    } finally {
      setStageSaving(false);
    }
  };
  
  // Handle stage selection change
  const handleStageSelectionChange = async (selectedOption) => {
    try {
      setStageSaving(true);
      setError(null);
      
      const updatedSettings = {
        ...autoStageChangeSettings,
        stageName: selectedOption ? selectedOption.value : '',
        stageLabel: selectedOption ? selectedOption.label : ''
      };
      
      const response = await updateAutoStageChangeSettings(updatedSettings);
      
      if (response.success) {
        setAutoStageChangeSettings(response.setting);
        setSuccess('Stage selection updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('Error updating stage selection:', error);
      setError('Failed to update stage selection');
    } finally {
      setStageSaving(false);
    }
  };

  // Save per-contact mappings
  const handleSavePerContactMappings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      console.log(`[DEBUG] Current perContactMappings state:`, perContactMappings);

      // Convert per-contact mappings to the format expected by the API
      const mappingsToUpdate = Object.entries(perContactMappings).map(([opportunityField, fieldMapping]) => ({
        opportunityField,
        mappingMode: 'per_contact',
        contactMappings: fieldMapping.contactMappings || []
      }));

      console.log('[DEBUG] Saving per-contact mappings:', mappingsToUpdate);
      
      // Filter out mappings with empty contact mappings for more informative logging
      const ownershipFields = mappingsToUpdate.filter(m => 
        m.opportunityField.includes('OwnershipPercentage')
      );
      console.log('[DEBUG] Ownership percentage fields being saved:', ownershipFields);

      if (mappingsToUpdate.length > 0) {
        const result = await bulkUpdatePerContactMappings(mappingsToUpdate);
        console.log('[DEBUG] API response:', result);
        setSuccess('Per-contact mappings saved successfully!');
        
        // Reload mappings to get updated data
        const updatedMappings = await getFieldMappings();
        setMappings(updatedMappings);
      } else {
        setSuccess('No per-contact mappings to save');
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving per-contact mappings:', error);
      setError('Failed to save per-contact mappings');
    } finally {
      setSaving(false);
    }
  };
  
  // Add debug button in development mode
  useEffect(() => {
    // Only add in non-production environment
    if (process.env.NODE_ENV !== 'production') {
      // Create button element
      const debugButton = document.createElement('button');
      debugButton.innerHTML = 'Debug Field Mappings';
      debugButton.style.position = 'fixed';
      debugButton.style.bottom = '10px';
      debugButton.style.right = '10px';
      debugButton.style.zIndex = '9999';
      debugButton.style.padding = '8px 12px';
      debugButton.style.backgroundColor = '#f8f9fa';
      debugButton.style.border = '1px solid #dee2e6';
      debugButton.style.borderRadius = '4px';
      debugButton.style.cursor = 'pointer';
      
      // Add click handler
      debugButton.addEventListener('click', () => {
        console.group('FIELD MAPPINGS DEBUG INFO');
        console.log('Current mappings:', mappings);
        console.log('Available fields per object:');
        Object.keys(salesforceFields).forEach(objName => {
          console.log(`${objName}: ${salesforceFields[objName].length} fields available`);
        });
        
        // Check for problematic mappings
        const problemMappings = mappings.filter(mapping => {
          if (!mapping.salesforceField) return false;
          
          const objectFields = salesforceFields[mapping.salesforceObject] || [];
          return !objectFields.some(field => field.name === mapping.salesforceField);
        });
        
        if (problemMappings.length > 0) {
          console.warn('Problem mappings detected:', problemMappings);
        } else {
          console.log('All mappings appear valid');
        }
        
        console.groupEnd();
      });
      
      // Add to document
      document.body.appendChild(debugButton);
      
      // Cleanup on unmount
      return () => {
        document.body.removeChild(debugButton);
      };
    }
  }, [mappings, salesforceFields]);
  
  if (loading && opportunityFields.length === 0) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Body className="text-center p-5">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <h5>Loading Field Mappings...</h5>
            <p className="text-muted">
              Retrieving Salesforce field metadata and existing mappings. This may take a few moments.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }
  
  return (
    <div className="page-container">
      <div className="field-mappings-container">
        {/* Sidebar */}
        <div className="field-mappings-sidebar">
          <FieldMappingsSideNav 
            categories={categories}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            mappingStats={calculateMappingStats()}
          />
        </div>
        
        {/* Main Content */}
        <div className="field-mappings-content">
          
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Body>
                  
                  
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                  )}
                  
                  {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>
                  )}
                  
                  <p className="text-muted small mb-0">
                    <i>Tip: You can type in the dropdowns to search for specific fields.</i>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Mapping Mode Toggle */}
          <MappingModeToggle 
            mode={mappingMode}
            onToggle={handleMappingModeToggle}
            disabled={modeChangePending || loading}
          />
          
          {/* Auto Stage Change Settings - Only show for General category */}
          {categories[activeSection] === 'General' && (
            <Card className="mb-3">
              <Card.Body style={{ padding: '1rem' }}>
                <Row className="mb-2">
                  <Col>
                    <h5 style={{ fontSize: '1rem' }}>
                      Auto Stage Change Settings
                      <Badge bg="info" className="ms-2">
                        Submission Automation
                      </Badge>
                    </h5>
                    <p className="text-muted small mb-3">
                      Automatically update the Opportunity Stage when submitting to lenders.
                    </p>
                  </Col>
                </Row>
                
                <Row className="align-items-center">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                        Enable Auto Stage Change
                      </Form.Label>
                      <Form.Check
                        type="switch"
                        id="auto-stage-enabled"
                        checked={autoStageChangeSettings.enabled}
                        onChange={(e) => handleAutoStageChangeToggle(e.target.checked)}
                        disabled={stageSaving}
                        label={autoStageChangeSettings.enabled ? 'Enabled' : 'Disabled'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                        Target Stage
                      </Form.Label>
                      <Select
                        value={
                          autoStageChangeSettings.stageName ? {
                            value: autoStageChangeSettings.stageName,
                            label: autoStageChangeSettings.stageLabel || autoStageChangeSettings.stageName
                          } : null
                        }
                        onChange={handleStageSelectionChange}
                        options={[
                          { value: '', label: 'Select a stage...' },
                          ...stageValues.map(stage => ({
                            value: stage.value,
                            label: stage.label
                          }))
                        ]}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        isSearchable={true}
                        placeholder="Select stage to change to..."
                        classNamePrefix="select"
                        isDisabled={!autoStageChangeSettings.enabled || stageSaving}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={3}>
                    {stageSaving && (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" />
                        <div className="small text-muted mt-1">Saving...</div>
                      </div>
                    )}
                    {autoStageChangeSettings.enabled && autoStageChangeSettings.stageName && (
                      <div className="text-success small">
                        <FaCheck className="me-1" />
                        Will change to: {autoStageChangeSettings.stageLabel}
                      </div>
                    )}
                    {autoStageChangeSettings.enabled && !autoStageChangeSettings.stageName && (
                      <div className="text-warning small">
                        <FaExclamationTriangle className="me-1" />
                        Please select a target stage
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Single unified mapping view (always uses shared mode UI, but respects mapping mode for categories) */}
          <Card>
            <Card.Body style={{ padding: '1rem' }}>
              <Row className="mb-2">
                <Col>
                  <h5 style={{ fontSize: '1rem' }}>
                    {categories[activeSection]} Fields
                    <Badge bg="primary" className="ms-2">
                      {getFilteredFields().length} 
                    </Badge>
                    <Badge bg={mappingMode === 'shared' ? 'success' : 'warning'} className="ms-2">
                      {mappingMode === 'shared' ? 'Shared Mode' : 'Per-Contact Mode'}
                    </Badge>
                  </h5>
                </Col>
              </Row>
              
              <ListGroup style={{ fontSize: '0.9rem' }}>
                {getFilteredFields().map((field, index) => {
                  const mapping = getMappingForField(field.name);
                  const isMapped = !!mapping.salesforceField;
                  const mappedClass = isMapped ? 'mapped-field' : '';
                  
                  return (
                    <ListGroup.Item 
                      key={`${field.name}-${mapping.salesforceObject || selectedObject}-${mapping.salesforceField || ''}`}
                      className={mappedClass}
                    >
                      <Row>
                        <Col md={2}>
                          <strong style={{ fontSize: '0.9rem' }}>{field.label || field.name}</strong>
                          <div className="text-muted small" style={{ fontSize: '0.8rem' }}>{field.name}</div>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Salesforce Object</Form.Label>
                            <Select
                              value={
                                salesforceObjects.find(obj => obj.value === (mapping.salesforceObject || selectedObject))
                              }
                              onChange={(option) => {
                                const newObject = option.value;
                                handleMappingObjectChange(field.name, { value: newObject });
                              }}
                              options={salesforceObjects}
                              styles={customStyles}
                              menuPortalTarget={document.body}
                              isSearchable={true}
                              placeholder="Select or search for an object..."
                              classNamePrefix="select"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={5}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Salesforce Field</Form.Label>
                            <Select
                              value={
                                (() => {
                                  const objectName = mapping.salesforceObject || selectedObject;
                                  const availableFields = salesforceFields[objectName] || [];
                                  const selectedField = mapping.salesforceField || '';
                                  const fieldOption = availableFields.find(f => f.name === selectedField);
                                  
                                  return selectedField ? {
                                    value: selectedField,
                                    label: fieldOption ? fieldOption.label || fieldOption.name : selectedField
                                  } : null;
                                })()
                              }
                              onChange={(option) => {
                                const newField = option ? option.value : '';
                                handleFieldChange(field.name, newField);
                              }}
                              options={
                                (() => {
                                  const objectName = mapping.salesforceObject || selectedObject;
                                  const availableFields = salesforceFields[objectName] || [];
                                  
                                  return [
                                    { value: '', label: 'Select Salesforce field' },
                                    ...availableFields.map(sfField => ({
                                      value: sfField.name,
                                      label: sfField.label || sfField.name
                                    }))
                                  ];
                                })()
                              }
                              styles={customStyles}
                              menuPortalTarget={document.body}
                              isSearchable={true}
                              placeholder="Select or search for a field..."
                              classNamePrefix="select"
                              key={`${field.name}-${mapping.salesforceObject || selectedObject}`}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Required</Form.Label>
                            <Form.Check
                              type="switch"
                              id={`required-${field.name}`}
                              checked={mapping.required !== false} // Default to true if not set
                              onChange={(e) => handleRequiredToggle(field.name, e.target.checked)}
                              label={mapping.required !== false ? 'Yes' : 'No'}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              
              {getFilteredFields().length === 0 && (
                <Alert variant="info" className="mt-3">
                  No fields found in this category.
                </Alert>
              )}
            </Card.Body>
            <Card.Footer style={{ padding: '0.75rem 1rem' }}>
              <Button 
                variant="primary" 
                onClick={handleSave}
                disabled={saving}
                className="me-2"
                size="sm"
              >
                {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : 'Save Mappings'}
              </Button>
              <Button 
                variant="info" 
                onClick={handleTest}
                disabled={testing}
                size="sm"
              >
                {testing ? <><Spinner size="sm" animation="border" className="me-1" /> Testing...</> : 'Test Mappings'}
              </Button>
            </Card.Footer>
          </Card>
          
          {/* Remove the old Primary Field Mapping Card since it's now handled conditionally above */}
          
        </div>
      </div>
      
      {/* Test Results Modal */}
      <Modal show={showTestResults} onHide={handleCloseTestResults} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Field Mapping Test Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {testResults && (
            <>
              <Alert variant={testResults.success ? "info" : "warning"}>
                {testResults.message}
              </Alert>
              
              <h6>Results:</h6>
              <ListGroup>
                {testResults.results && testResults.results.map((result, index) => (
                  <ListGroup.Item 
                    key={index}
                    variant={result.valid ? "success" : "danger"}
                  >
                    <Row>
                      <Col xs={1}>
                        {result.valid ? (
                          <FaCheck className="text-success" />
                        ) : (
                          <FaTimes className="text-danger" />
                        )}
                      </Col>
                      <Col>
                        <strong>{result.opportunityField}</strong>
                        {' → '}
                        <strong>{result.salesforceObject}.{result.salesforceField}</strong>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTestResults}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FieldMappings; 